import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, UserPlus, Check, X, Users, Heart, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { api, errMsg } from '../lib/api';
import { Avatar, ProgressRing } from '../components/SocialBits';
import { SocialVaultDialog, GroupContributeDialog } from '../components/SocialDialogs';

const Empty = ({ icon: Icon, title, hint, testid }) => (
  <div data-testid={testid} className="p-10 text-center rounded-xl border border-white/10 bg-[#0A0A0F]">
    <Icon className="h-7 w-7 text-[#9333EA] mx-auto mb-2" /><p className="text-white font-semibold text-sm">{title}</p><p className="text-[#A19DB0] text-xs mt-1">{hint}</p>
  </div>
);

function FriendsTab({ data, reload }) {
  const [email, setEmail] = useState('');
  const add = async () => {
    if (!email.trim()) return;
    try { const r = await api.post('/friends/request', { email }); toast.success(r.data.status === 'accepted' ? 'You are now friends!' : r.data.status === 'already_friends' ? 'Already friends' : 'Friend request sent'); setEmail(''); reload(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const act = async (fn) => { try { await fn(); reload(); } catch (err) { toast.error(errMsg(err)); } };
  const Row = ({ f, children }) => (
    <div className="flex items-center gap-3 p-3 rounded-xl border border-white/10 bg-[#0A0A0F]" data-testid={`friend-row-${f.user_id}`}>
      <Avatar user={f} size={9} /><span className="text-sm text-white font-medium flex-1">{f.name}</span>{children}
    </div>
  );
  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        <Input data-testid="add-friend-email-input" value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Friend's FutureVault email" className="input-dark h-11" />
        <button data-testid="add-friend-btn" onClick={add} className="shrink-0 px-4 rounded-lg bg-[#9333EA] text-white text-sm font-semibold hover:bg-[#7E22CE] transition-colors flex items-center gap-1.5"><UserPlus className="h-4 w-4" />Add</button>
      </div>
      {data.incoming.length > 0 && (
        <section className="space-y-2" data-testid="incoming-requests">
          <h3 className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">Requests</h3>
          {data.incoming.map((f) => (
            <Row key={f.friendship_id} f={f}>
              <button data-testid={`accept-friend-${f.friendship_id}`} onClick={() => act(() => api.post(`/friends/${f.friendship_id}/accept`))} className="h-8 w-8 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center hover:bg-emerald-500/30"><Check className="h-4 w-4" /></button>
              <button data-testid={`decline-friend-${f.friendship_id}`} onClick={() => act(() => api.post(`/friends/${f.friendship_id}/decline`))} className="h-8 w-8 rounded-full bg-white/5 text-[#A19DB0] flex items-center justify-center hover:text-white"><X className="h-4 w-4" /></button>
            </Row>
          ))}
        </section>
      )}
      <section className="space-y-2" data-testid="friends-list">
        <h3 className="text-[10px] uppercase tracking-widest text-[#A19DB0] font-bold">Friends · {data.friends.length}</h3>
        {data.friends.length === 0 ? <Empty testid="friends-empty" icon={Users} title="No friends yet" hint="Add a friend by the email they use on FutureVault." /> :
          data.friends.map((f) => (
            <Row key={f.friendship_id} f={f}>
              <button data-testid={`remove-friend-${f.user_id}`} onClick={() => act(() => api.delete(`/friends/${f.user_id}`))} className="text-[11px] text-[#6F6981] hover:text-red-300 transition-colors">Remove</button>
            </Row>
          ))}
        {data.outgoing.map((f) => <Row key={f.friendship_id} f={f}><span className="text-[11px] text-[#6F6981]">Pending</span></Row>)}
      </section>
    </div>
  );
}

function FeedTab({ onOpen }) {
  const [vaults, setVaults] = useState(null);
  useEffect(() => { api.get('/social/feed').then((r) => setVaults(r.data.vaults)).catch(() => setVaults([])); }, []);
  if (!vaults) return null;
  if (vaults.length === 0) return <Empty testid="feed-empty" icon={Heart} title="Nothing to cheer yet" hint="When friends share a vault with you, it shows up here." />;
  return (
    <div className="space-y-2" data-testid="social-feed-list">
      {vaults.map((v) => (
        <button key={v.goal_id} data-testid={`feed-vault-${v.goal_id}`} onClick={() => onOpen(v.goal_id)} className="w-full flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-[#0A0A0F] hover:border-[#9333EA]/40 transition-colors text-left">
          <ProgressRing percent={v.progress_percent} />
          <div className="flex-1 min-w-0">
            <p className="text-white font-semibold truncate">{v.name}{v.is_group && <span className="ml-2 text-[10px] text-[#C8A6FF] uppercase">Group</span>}</p>
            <p className="text-xs text-[#A19DB0] flex items-center gap-1.5 mt-0.5"><Avatar user={v.owner} size={4} />{v.owner.name} · {v.days_left}d left</p>
          </div>
          <span className="text-[11px] text-[#A19DB0]">{v.is_following ? 'Following' : ''}</span>
        </button>
      ))}
    </div>
  );
}

function GroupsTab({ onContribute }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get('/groups/mine').then((r) => setData(r.data)).catch(() => setData({ groups: [], invites: [] })), []);
  useEffect(() => { load(); }, [load]);
  const respond = async (id, accept) => { try { await api.post(`/groups/${id}/respond`, { accept }); load(); } catch (err) { toast.error(errMsg(err)); } };
  const leave = async (id) => { try { await api.post(`/groups/${id}/leave`); load(); } catch (err) { toast.error(errMsg(err)); } };
  if (!data) return null;
  return (
    <div className="space-y-5">
      {data.invites.map((g) => (
        <div key={g.goal_id} data-testid={`group-invite-${g.goal_id}`} className="p-4 rounded-xl border border-amber-500/30 bg-amber-500/5">
          <p className="text-sm text-white"><span className="font-semibold">{g.owner.name}</span> invited you to <span className="font-semibold">{g.name}</span></p>
          <p className="text-[11px] text-[#A19DB0] mt-1">{g.owner.name} holds the money and is the only one who can withdraw it.</p>
          <div className="flex gap-2 mt-3">
            <button data-testid={`group-accept-${g.goal_id}`} onClick={() => respond(g.goal_id, true)} className="px-4 py-1.5 rounded-full bg-[#9333EA] text-white text-xs font-semibold">Join</button>
            <button data-testid={`group-decline-${g.goal_id}`} onClick={() => respond(g.goal_id, false)} className="px-4 py-1.5 rounded-full border border-white/15 text-[#A19DB0] text-xs">Decline</button>
          </div>
        </div>
      ))}
      {data.groups.length === 0 && data.invites.length === 0 ? <Empty testid="groups-empty" icon={Users} title="No group vaults" hint="Open one of your vaults and invite friends to save together." /> :
        data.groups.map((g) => (
          <div key={g.goal_id} data-testid={`group-card-${g.goal_id}`} className="p-4 rounded-xl border border-white/10 bg-[#0A0A0F]">
            <div className="flex items-center gap-4">
              <ProgressRing percent={g.progress_percent} />
              <div className="flex-1">
                <p className="text-white font-semibold">{g.name}</p>
                <p className="text-xs text-[#A19DB0]">${g.current_amount.toFixed(2)} of ${Number(g.target_amount).toFixed(2)} · {g.members.filter((m) => m.status === 'active').length + 1} members</p>
                <p className="text-xs text-[#C8A6FF] mt-0.5" data-testid={`group-my-contribution-${g.goal_id}`}>You've added ${Number(g.my_contribution || 0).toFixed(2)}</p>
              </div>
            </div>
            {!g.is_owner && (
              <div className="flex gap-2 mt-3">
                <button data-testid={`group-contribute-${g.goal_id}`} onClick={() => onContribute(g)} className="flex-1 h-9 rounded-lg btn-primary text-xs font-semibold">Add money</button>
                <button data-testid={`group-leave-${g.goal_id}`} onClick={() => leave(g.goal_id)} className="px-3 h-9 rounded-lg border border-white/10 text-[#A19DB0] text-xs hover:text-white">Leave</button>
              </div>
            )}
          </div>
        ))}
    </div>
  );
}

export default function FriendsPage() {
  const [data, setData] = useState({ friends: [], incoming: [], outgoing: [] });
  const [openVault, setOpenVault] = useState(null);
  const [contribute, setContribute] = useState(null);
  const reload = useCallback(() => api.get('/friends').then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { reload(); }, [reload]);
  return (
    <div className="min-h-screen bg-vault" data-testid="friends-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <Link to="/dashboard" data-testid="friends-back-link" className="text-[#A19DB0] hover:text-white transition-colors"><ArrowLeft className="h-5 w-5" /></Link>
          <span className="font-heading text-lg font-bold text-white tracking-tight">Friends & Groups</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-6">
        <p className="text-[#A19DB0] text-sm mb-5 flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Friends only see vaults you choose to share — and never your dollar amounts.</p>
        <Tabs defaultValue="friends">
          <TabsList className="bg-[#0A0A0F] border border-white/10 mb-5">
            <TabsTrigger data-testid="tab-friends" value="friends">Friends{data.incoming.length > 0 && <span className="ml-1.5 h-4 min-w-4 px-1 rounded-full bg-amber-500 text-black text-[10px] font-bold">{data.incoming.length}</span>}</TabsTrigger>
            <TabsTrigger data-testid="tab-feed" value="feed">Cheer them on</TabsTrigger>
            <TabsTrigger data-testid="tab-groups" value="groups">Group vaults</TabsTrigger>
          </TabsList>
          <TabsContent value="friends"><FriendsTab data={data} reload={reload} /></TabsContent>
          <TabsContent value="feed"><FeedTab onOpen={setOpenVault} /></TabsContent>
          <TabsContent value="groups"><GroupsTab onContribute={setContribute} /></TabsContent>
        </Tabs>
      </main>
      <SocialVaultDialog goalId={openVault} onClose={() => setOpenVault(null)} />
      <GroupContributeDialog group={contribute} onClose={() => setContribute(null)} />
    </div>
  );
}
